<script>
  import { navigate } from 'svelte-routing';
  import { getApiUrl } from '../api';
  import { authStore } from '../authStore';
  import { authFetch } from '../authService';
  import StoreAutocomplete from '../components/StoreAutocomplete.svelte';

  let isLoading = false;
  let error = '';
  let fileInput;
  let currentUser = null;

  // Data State
  let storeName = '';
  let extractedProducts = [];
  let isEditing = true; // Show table immediately after scan

  authStore.subscribe(value => {
    currentUser = value.user;
  });

  async function handleFileChange(e) {
    const file = e.target.files[0];
    if (!file) return;
    isLoading = true;
    error = '';
    
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = async () => {
      const base64String = reader.result.split(',')[1];
      try {
        const response = await authFetch(getApiUrl('/products/scan-bulk'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ imageBase64: base64String }),
        });
        if (!response.ok) throw new Error('Failed to scan image.');
        
        extractedProducts = await response.json();
        
        // Auto-calculate percentages for all found items
        extractedProducts = extractedProducts.map(p => {
             // Ensure valid numbers
             p.originalPrice = p.originalPrice || '';
             p.discountPrice = p.discountPrice || '';
             p.description = p.description || '';
             calculateDiscount(p);
             return p;
        });

      } catch (err) {
        error = err.message;
      } finally {
        isLoading = false;
      }
    };
  }

  function calculateDiscount(item) {
    const orig = parseFloat(item.originalPrice);
    const disc = parseFloat(item.discountPrice);
    if (orig > 0 && disc >= 0 && orig > disc) {
      item.discountPercentage = (((orig - disc) / orig) * 100).toFixed(2);
    } else {
      item.discountPercentage = '';
    }
  }

  function removeRow(index) {
      extractedProducts = extractedProducts.filter((_, i) => i !== index);
  }

  function addRow() {
      extractedProducts = [...extractedProducts, { productName: '', originalPrice: '', discountPrice: '', description: '' }];
  }

  async function handleSaveAll() {
    if (!storeName) {
        error = "Please enter a Store Name.";
        return;
    }
    if (extractedProducts.length === 0) {
        error = "No products to save.";
        return;
    }

    isLoading = true;
    error = '';

    // Prepare payload
    const payload = extractedProducts.map(p => ({
        productName: p.productName,
        storeName: storeName, // Use the single store name for all
        originalPrice: p.originalPrice ? parseFloat(p.originalPrice) : null,
        discountPrice: p.discountPrice ? parseFloat(p.discountPrice) : null,
        description: p.description,
        discountPercentage: p.discountPercentage ? parseFloat(p.discountPercentage) : null,
        uploadedByUserId: currentUser?.idTokenClaims?.oid,
        // Optional: countryCode could be added here if needed
    }));

    try {
        const res = await authFetch(getApiUrl('/products/bulk'), {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify(payload)
        });
        if (!res.ok) throw new Error("Failed to save.");
        
        alert(`Successfully saved ${extractedProducts.length} products!`);
        navigate('/products/search');
    } catch(e) {
        error = e.message;
    } finally {
        isLoading = false;
    }
  }
</script>

<div class="bulk-container">
  <h2>Bulk Scan Flyer</h2>
  <p class="subtitle">Upload a flyer to automatically extract multiple product discounts at once.</p>

  {#if extractedProducts.length === 0}
      <!-- Initial Upload State -->
      <div class="upload-area">
        <input
            type="file"
            accept="image/png, image/jpeg"
            on:change={handleFileChange}
            bind:this={fileInput}
            style="display: none;"
        />
        <button class="upload-btn" on:click={() => fileInput.click()} disabled={isLoading}>
            {isLoading ? 'Analyzing Flyer...' : '📁 Upload Flyer Image'}
        </button>
      </div>
  {:else}
      <!-- Editor State -->
      <div class="editor-area">
          <div class="store-input">
              <label>Store Name (Applied to all items):</label>
              <StoreAutocomplete bind:value={storeName} placeholder="e.g. Walmart" required={true} />
          </div>

          <div class="table-wrapper">
              <table>
                  <thead>
                      <tr>
                          <th>Product Name</th>
                          <th>Orig. $</th>
                          <th>Disc. $</th>
                          <th>Description</th>
                          <th>Action</th>
                      </tr>
                  </thead>
                  <tbody>
                      {#each extractedProducts as item, i}
                          <tr>
                              <td><input type="text" bind:value={item.productName} /></td>
                              <td><input type="number" step="0.01" bind:value={item.originalPrice} on:input={() => calculateDiscount(item)} class="price-input" /></td>
                              <td><input type="number" step="0.01" bind:value={item.discountPrice} on:input={() => calculateDiscount(item)} class="price-input" /></td>
                              <td><input type="text" bind:value={item.description} /></td>
                              <td><button class="delete-btn" on:click={() => removeRow(i)}>X</button></td>
                          </tr>
                      {/each}
                  </tbody>
              </table>
          </div>
          
          <button class="add-row-btn" on:click={addRow}>+ Add Row</button>

          <div class="actions">
              <button class="cancel-btn" on:click={() => extractedProducts = []}>Cancel / Re-scan</button>
              <button class="save-btn" on:click={handleSaveAll} disabled={isLoading}>
                  {isLoading ? 'Saving...' : 'Save All Products'}
              </button>
          </div>
      </div>
  {/if}

  {#if error} <p class="error">{error}</p> {/if}
</div>

<style>
  .bulk-container { max-width: 1000px; margin: 2rem auto; padding: 0 1rem; }
  .subtitle { color: #666; margin-bottom: 2rem; }
  
  .upload-area { text-align: center; padding: 4rem; border: 2px dashed #ccc; border-radius: 8px; background: #f9f9f9; }
  .upload-btn { padding: 1rem 2rem; font-size: 1.2rem; background-color: #007bff; color: white; border: none; border-radius: 8px; cursor: pointer; }
  
  .store-input { margin-bottom: 1.5rem; max-width: 400px; }
  .store-input label { display: block; font-weight: bold; margin-bottom: 0.5rem; }

  .table-wrapper { overflow-x: auto; background: white; border-radius: 8px; box-shadow: 0 2px 4px rgba(0,0,0,0.05); }
  table { width: 100%; border-collapse: collapse; min-width: 600px; }
  th, td { padding: 0.8rem; border-bottom: 1px solid #eee; text-align: left; }
  th { background: #f8f9fa; font-weight: 600; }
  
  input { width: 100%; padding: 0.5rem; border: 1px solid #ccc; border-radius: 4px; box-sizing: border-box; }
  .price-input { width: 80px; }
  
  .delete-btn { background: #dc3545; color: white; border: none; padding: 0.3rem 0.6rem; border-radius: 4px; cursor: pointer; }
  .add-row-btn { background: none; border: 1px dashed #999; color: #666; width: 100%; padding: 0.8rem; margin-top: 0.5rem; cursor: pointer; }
  .add-row-btn:hover { background: #f0f0f0; }

  .actions { display: flex; justify-content: flex-end; gap: 1rem; margin-top: 2rem; }
  .cancel-btn { background: #6c757d; color: white; border: none; padding: 0.8rem 1.5rem; border-radius: 4px; cursor: pointer; }
  .save-btn { background: #28a745; color: white; border: none; padding: 0.8rem 1.5rem; border-radius: 4px; cursor: pointer; font-size: 1.1rem; }
  
  .error { color: red; text-align: center; margin-top: 1rem; }
</style>
